import type React from 'react';
import type { ReactNode } from 'react';

import type { MenuOptions } from './menu-wrapper';

import classNames from 'classnames';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';

import { MenuHeader } from './menu-header';
import { ChevronIcon } from '../../icons/chevron';

import styles from '../menu/menu.module.css';

type MenuProps = {
  label: ReactNode;
  onGoBack?: () => void;
  onClose?: () => void;
  hasParent?: boolean;
  showHeader?: boolean;
  dataTestId?: string;
  children?: ReactNode | undefined;
  headerSubmenu?: MenuOptions;
  onHeaderSubmenuClick?: React.MouseEventHandler<HTMLButtonElement> | undefined;
};

export const Menu = forwardRef(function Menu(
  {
    label,
    hasParent = false,
    showHeader,
    dataTestId = '',
    children,
    onGoBack,
    onClose,
    headerSubmenu,
    onHeaderSubmenuClick,
  }: MenuProps,
  ref: React.Ref<HTMLDivElement>
) {
  const { t } = useTranslation();

  return (
    <div role="menu" className={styles.menu} data-testid={dataTestId}>
      <MenuHeader
        data-testid="menu-header"
        label={label}
        leftIcon={
          hasParent && (
            <button
              onClick={onGoBack}
              data-testid="menu-back-button"
              aria-label={t('ui.menu.goback.label')}
              className={styles.goBackButton}
            >
              <ChevronIcon />
            </button>
          )
        }
        rightIcon={
          headerSubmenu && (
            <button
              onClick={onHeaderSubmenuClick}
              data-goto={headerSubmenu.id}
              data-testid={headerSubmenu.id}
              className={styles.submenuButton}
            >
              <span>
                {headerSubmenu.label} {headerSubmenu.options?.length && <ChevronIcon />}
              </span>
            </button>
          )
        }
        onClick={hasParent ? onGoBack : undefined}
        onClose={onClose}
        className={classNames(
          styles.menuHeader,
          showHeader === true && styles.showHeader,
          showHeader === false && styles.hideHeader,
          !hasParent && styles.disabled
        )}
      />

      {children && (
        <div ref={ref} data-testid="menu-scroll" className={classNames(styles.menuItemsWrapper, 'player-scrollbar')}>
          {children}
        </div>
      )}
    </div>
  );
});
